import '../alert/alert';
import '../popover/popover';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { ConnectLaunchError } from './utils/connect-launch.utils';
import { connect } from 'pwa-helpers';
import { customElement, property, query, state } from 'lit/decorators.js';
import { findClosest } from '../../common/utils/util';
import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { getLaunchConnectError, getLaunchConnectStatus, getLaunchConnectUrl } from './connect-launch.selector';
import { StoreState } from '../../store/store.interface';
import { submitConnectSection } from './connect-launch.thunk';
import { ActionStatus } from '../../common/constants/action-status';

@customElement('mhecomm-connect-launch-modal')
export class ConnectLaunchModal extends connect(store)(BaseComponent) {
  @property()
  submitLabel = 'Apply';

  @property()
  submittingLabel = 'Applying';

  @property()
  placeholder = 'Enter Connect section url';

  @property()
  launchLabel = 'Launch Connect';

  @property()
  header = 'Enter your Connect section web address';

  @property()
  desc =
    'Please enter the Connect section web address you received from your instructor. Contact your instructor if you have not yet received it.';

  @property()
  moreInfoLabel = 'What is this for?';

  @property()
  moreInfoDesc = 'This address allows you to register for your class so you can do your homework.';

  @property()
  isbn?: string;

  @property()
  orderId?: string;

  @property()
  connectLaunchUrl?: string;

  @property()
  buttonStyle = 'btn-primary';

  @property()
  icon?: string;

  @property()
  triggerAriaLabel?: string;

  @state()
  private sectionUrl?: string;

  @state()
  private loading = false;

  @state()
  private formError?: string;

  @query('input[type="text"]')
  private inputElement?: HTMLInputElement;

  @query('.btn-close')
  private closeButtonElement?: HTMLElement;

  @query('.launch-button')
  private launchButton?: HTMLElement;

  modal: any;
  refocusListener?: EventListenerOrEventListenerObject;

  renderComp(): TemplateResult {
    return html`
      <button
        type="button"
        data-automation-id="btn-launch-${this.orderId}-${this.isbn}"
        class="btn ${this.buttonStyle} launch-button"
        @click=${this.openModal}
        aria-haspopup="dialog"
        aria-label=${ifDefined(this.triggerAriaLabel)}>
        ${this.launchLabel} ${this.icon ? html`<uxt-icon icon=${this.icon}></uxt-icon>` : ''}
      </button>
      <div
        class="modal show fade pt-5"
        @focusout=${(e: any) => this.refocus(e.relatedTarget)}
        tabindex="-1"
        aria-hidden="false"
        aria-modal="true"
        aria-labelledby="connect-launch-modal">
        <div class="modal-dialog modal-fullscreen-md-down" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="connect-launch-modal">${this.header}</h5>
              <button
                type="button"
                @click=${this.closeModal}
                data-bs-dismiss="modal"
                class="btn-close"
                aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div>${this.desc}</div>
              <mhecomm-popover
                id="what-is-this-for"
                class="text-end"
                label=${this.moreInfoLabel}
                text=${this.moreInfoDesc}
                ariaLabel=${this.moreInfoLabel}>
              </mhecomm-popover>
              <input
                type="text"
                class="form-control ${this.formError ? 'is-invalid' : ''}"
                aria-label=${this.placeholder}
                aria-invalid=${ifDefined(this.formError ? 'true' : undefined)}
                aria-describedby=${ifDefined(this.formError ? 'connect-form-error' : undefined)}
                @input=${(e: any) => this.inputChange(e.target.value)}
                placeholder=${this.placeholder} />
              ${this.formError
                ? html`
                    <p id="connect-form-error" class="invalid-feedback" role="alert">
                      ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle" aria-hidden="true"></uxt-icon>` : nothing}
                      ${this.formError}
                    </p>
                  `
                : nothing}
            </div>
            <div class="modal-footer">
              <button
                type="button"
                @click=${() => this.confirm()}
                ?disabled=${this.loading}
                class="btn btn-primary"
                aria-label="Apply Connect section URL">
                ${this.loading
                  ? html`
                      ${this.submittingLabel}
                      <div class="spinner-border spinner-border-sm ms-2" role="status"></div>
                    `
                  : this.submitLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  stateChanged(state: StoreState) {
    const status = getLaunchConnectStatus(state);

    if (status === ActionStatus.loading) {
      this.formError = undefined;
      this.loading = true;
    } else if (status === ActionStatus.succeeded) {
      this.formError = undefined;
      this.loading = false;
    } else if (status === ActionStatus.failed) {
      this.loading = false;
      this.handleError();
    }
  }

  inputChange(sectionUrl: string): void {
    this.sectionUrl = sectionUrl;
    this.formError = undefined;
  }

  openModal(): void {
    if (this.isbn) {
      if (this.connectLaunchUrl) {
        window.open(this.connectLaunchUrl);
      } else {
        this.showModal();
      }
    } else {
      this.focusInput();
      this.formError = ConnectLaunchError.NO_ISBN_FOUND;
    }
  }

  private showModal(): void {
    if (!this.modal) {
      this.initModal();
      return;
    }

    this.modal.show();
    this.dispatchEvent(new CustomEvent('opened-modal'));
  }

  confirm(): void {
    if (this.sectionUrl) {
      this.focusInput();
      this.loading = true;
      this.formError = undefined;
      store.dispatch(submitConnectSection({ sectionUrl: this.sectionUrl }))
        .then(() => this.handleSuccess())
        .catch(() => this.focusInput());
    } else if (!this.isbn) {
      this.focusInput();
      this.formError = ConnectLaunchError.NO_ISBN_FOUND;
    } else if (!this.sectionUrl) {
      this.focusInput();
      this.formError = ConnectLaunchError.PLEASE_PROVIDE_SECTION_URL;
    } else {
      this.focusInput();
      this.formError = ConnectLaunchError.UNKNOWN_ERROR;
    }
  }

  private handleSuccess(): void {
    const url = getLaunchConnectUrl(store.getState() as StoreState);
    if (url) {
      const protocol = url.startsWith('http') ? '' : 'https://';
      window.open(protocol + url);
      this.modal.hide();
      if (this.inputElement) this.inputElement.value = '';
    } else {
      this.focusInput();
      this.formError = ConnectLaunchError.COULD_NOT_FIND_SECTION_URL;
    }
  }

  private focusLaunchButton(): void {
    // No kidding, this focus only works when it is called twice... Please don't remove the second call!
    this.launchButton?.focus();
    this.launchButton?.focus();
  }

  private focusInput(): void {
    // No kidding, this focus only works when it is called twice... Please don't remove the second call!
    this.inputElement?.focus();
    this.inputElement?.focus();
  }

  private focusCloseBtn(): void {
    // No kidding, this focus only works when it is called twice... Please don't remove the second call!
    this.closeButtonElement?.focus();
    this.closeButtonElement?.focus();
  }

  private handleError() {
    this.focusInput();
    const error = getLaunchConnectError(store.getState() as StoreState);

    if (error) {
      this.formError = error;
    } else {
      this.formError = ConnectLaunchError.UNKNOWN_ERROR;
    }
  }

  private closeModal(): void {
    this.modal.hide();
  }

  refocus(target: HTMLElement): void {
    if (!findClosest(target, '.modal')) {
      this.focusCloseBtn();
    }
  }

  initModal(): void {
    this.modal = this.bsInit('.modal', window.bootstrap?.Modal)?.instance;
    const modalEl = this.renderRoot.querySelector<HTMLElement>('.modal');
    if (modalEl && !modalEl.dataset['focusListenersAttached']) {
      modalEl.dataset['focusListenersAttached'] = 'true';
      modalEl.addEventListener('shown.bs.modal', () => this.focusInput());
      modalEl.addEventListener('hidden.bs.modal', () => this.focusLaunchButton());
    }
  }

  updated(): void {
    this.initModal();
  }
}
